import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { TextInput } from "@/components/design/TextField";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";
import {
  AgentDocument,
  CreateAgentDocument,
  DeleteAgentDocument,
  GlobalSearchDocument,
  SearchEntityType,
  UpdateAgentDocument,
} from "@/graphql/generated/operations";
import { isMissingEntityError, validationResultMessage } from "@/lib/graphql-error";
import { LocalizedError } from "@/lib/localized-error";
import { userErrorMessage } from "@/lib/user-error";
import { useUnsavedEditsGate } from "@/lib/unsaved-edits-gate";
import { useDebounced } from "@/lib/use-debounced";
import { useGlobalSearch } from "@/lib/use-global-search";
import { AdminMasterDetail } from "./AdminMasterDetail";
import { AgentEditor } from "./AgentEditor";
import { draftToInput } from "./agent-draft";
import type { AgentDraft } from "./agent-draft";

const PAGE_SIZE = 50;

// Admin > Stammdaten > KI-Agenten: the instance's AI agents (genesis#408). Its own list and editor
// rather than an EDITABLE_REF_TYPES entry, because an agent carries prose in two languages and four
// repeatable lists, which the flat label/id shape cannot hold. Delete is hard; a match keeps the
// snapshot it stored (@adr SPARK-0034-ai-agents-are-project-context-not-team-members@2).
//
// The list scales to about 1000 agents: it is `globalSearch` over AGENT, a page at a time with a
// search box, and shows the index label (the name) without loading any agent. Only the selected
// agent is read in full, by id.
export function AgentsPanel() {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 250);
  const trimmed = debounced.trim();
  const input = useMemo(
    () => ({ query: trimmed.length > 0 ? trimmed : null, entityTypes: [SearchEntityType.Agent] }),
    [trimmed],
  );
  const list = useGlobalSearch({ input, pageSize: PAGE_SIZE });
  const entries = list.results;

  // The mutation's own answer is the outcome. The list refetch after it only re-syncs labels, order
  // and other admins' edits; it is not awaited, so its failure surfaces as the list-load error above
  // the panel, never as a failed save or delete.
  const refetch = { refetchQueries: [GlobalSearchDocument] };
  const [createAgent] = useMutation(CreateAgentDocument, {
    ...refetch,
    // Seed `agent(id)` from the answer, so the new agent's editor opens without another read.
    update(cache, { data }) {
      const created = data?.createAgent.__typename === "CreateAgentResult" ? data.createAgent.result : null;
      if (!created) return;
      cache.writeQuery({ query: AgentDocument, variables: { id: created.id }, data: { __typename: "Query", agent: created } });
    },
  });
  // An update's answer normalises onto the existing Agent row in the cache by itself.
  const [updateAgent] = useMutation(UpdateAgentDocument, refetch);
  const [deleteAgent] = useMutation(DeleteAgentDocument, {
    ...refetch,
    // The row is gone for good: drop it from the cache too, or `/a/<id>` would still paint it
    // from there before its own read comes back empty.
    update(cache, { data }) {
      const id = data?.deleteAgent.id;
      if (!id) return;
      cache.evict({ id: cache.identify({ __typename: "Agent", id }) });
      cache.gc();
    },
  });
  // `agentId` is the hand-off from `/a/:id`'s edit link. Otherwise null = the create form stands on
  // the right, as in the Power-Scorecard tab.
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get("agentId"));
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const leaveGate = useUnsavedEditsGate();

  const detail = useQuery(AgentDocument, { variables: { id: selectedId ?? "" }, skip: selectedId === null });
  const selected = selectedId !== null ? (detail.data?.agent ?? null) : null;
  // An agent genesis says does not exist (deleted, or a stale deep link) leaves no empty detail
  // column behind. A failed read proves nothing: the selection then stays under the error alert
  // until the admin picks something.
  const missing =
    selectedId !== null &&
    !detail.loading &&
    ((detail.data != null && detail.data.agent == null) || (detail.error != null && isMissingEntityError(detail.error)));
  if (missing) setSelectedId(null);
  const detailError = detail.error && !isMissingEntityError(detail.error) ? detail.error : null;
  const waitingForSelection = selectedId !== null && !selected;

  const saveFailed = intl.formatMessage({
    id: "admin.agents.saveFailed",
    defaultMessage: "The AI agent could not be saved.",
  });

  async function save(draft: AgentDraft) {
    const data = draftToInput(draft);
    let payload;
    try {
      payload = selected
        ? (await updateAgent({ variables: { id: selected.id, input: { data } } })).data?.updateAgent
        : (await createAgent({ variables: { input: { data } } })).data?.createAgent;
    } catch (cause) {
      throw new LocalizedError(userErrorMessage(intl, cause), { cause });
    }
    if (payload?.__typename === "ValidateAgentResult") {
      throw new LocalizedError(validationResultMessage(payload, saveFailed));
    }
    if (payload?.__typename === "CreateAgentResult") setSelectedId(payload.result.id);
  }

  async function remove(id: string) {
    setDeleteError(null);
    try {
      await deleteAgent({ variables: { input: { id } } });
      setSelectedId(null);
    } catch (cause) {
      setDeleteError(userErrorMessage(intl, cause));
    }
  }

  const select = (id: string | null) => {
    if (id === selectedId) return;
    leaveGate.request(() => {
      setDeleteError(null);
      setSelectedId(id);
    });
  };

  const title = intl.formatMessage({ id: "stammdaten.agents", defaultMessage: "AI agents" });

  return (
    <div className="space-y-4">
      {list.error ? (
        <p role="alert" className="smycles-field-meta text-ink">{userErrorMessage(intl, list.error)}</p>
      ) : null}
      {detailError ? (
        <p role="alert" className="smycles-field-meta text-ink">{userErrorMessage(intl, detailError)}</p>
      ) : null}
      {deleteError ? (
        <p role="alert" className="smycles-field-meta text-ink">{deleteError}</p>
      ) : null}
      <AdminMasterDetail
        title={title}
        list={
          <>
            <Button variant="primary" className="mb-3 w-full" onClick={() => select(null)}>
              {intl.formatMessage({ id: "admin.agents.new", defaultMessage: "New AI agent" })}
            </Button>
            <TextInput
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={intl.formatMessage({ id: "match.agent.searchLabel", defaultMessage: "Search agents" })}
              placeholder={intl.formatMessage({ id: "match.agent.searchPlaceholder", defaultMessage: "Name, role, tool …" })}
              className="mb-3"
            />
            {list.loading && entries.length === 0 ? (
              <div className="smycles-field-meta text-ink-muted">
                {intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })}
              </div>
            ) : entries.length === 0 && !list.error ? (
              <div className="smycles-field-meta text-ink-muted">
                {trimmed
                  ? intl.formatMessage({ id: "search.noResults", defaultMessage: "No matches" })
                  : intl.formatMessage({ id: "admin.agents.empty", defaultMessage: "No AI agents yet." })}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {entries.map((entry) => (
                  <MenuItem
                    key={entry.entityId}
                    active={entry.entityId === selectedId}
                    aria-pressed={entry.entityId === selectedId}
                    onClick={() => select(entry.entityId)}
                  >
                    {entry.label ?? "–"}
                  </MenuItem>
                ))}
              </div>
            )}
            {list.hasMore ? (
              <div className="mt-3 text-center">
                <Button variant="secondary" size="sm" disabled={list.loadingMore} onClick={() => list.loadMore()}>
                  {intl.formatMessage({ id: "common.loadMore", defaultMessage: "Load more" })}
                </Button>
                {list.loadMoreFailed ? (
                  <div role="alert" className="smycles-field-meta mt-1 text-ink">
                    {intl.formatMessage({ id: "match.agent.loadMoreFailed", defaultMessage: "The next agents could not be loaded." })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        }
      >
        {/* A deep link names an agent the list has not delivered yet: wait for it rather than
            flash the create form in its place. After a failed read the error alert above says
            why; the create form comes only from "New AI agent". */}
        {waitingForSelection ? (
          detailError ? null : (
            <div className="box bg-surface p-5 smycles-field-meta text-ink-muted" role="status">
              {intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })}
            </div>
          )
        ) : (
          <AgentEditor
            key={selected?.id ?? "new"}
            agent={selected}
            onSave={save}
            onDelete={() => (selected ? remove(selected.id) : Promise.resolve())}
          />
        )}
      </AdminMasterDetail>
      <UnsavedChangesDialog
        open={leaveGate.blocked}
        saving={leaveGate.saving}
        error={leaveGate.error}
        onStay={leaveGate.stay}
        onDiscard={leaveGate.discardAndProceed}
        onSave={leaveGate.saveAndProceed}
      />
    </div>
  );
}
